import { CSS_CLASSES, MESSAGES } from "../constants/app";
import type { PatientNoteView } from "../types";
import { formatNoteDateTime } from "../utils/formatNoteDateTime";
import { NoteDeleteIcon, NoteEditIcon, TabNotesIcon } from "./icons/patientDetailIcons";
import { InlineLoading } from "./loading/LoadingIndicators";

export interface NotesTabProps {
  notes: PatientNoteView[];
  notesLoading: boolean;
  notesError: string | null;
  onRetry: () => void;
  onEditNote: (noteId: string, text: string) => void;
  onDeleteNote: (noteId: string) => void;
  onAddNote: () => void;
}

export default function NotesTab({
  notes,
  notesLoading,
  notesError,
  onRetry,
  onEditNote,
  onDeleteNote,
  onAddNote,
}: NotesTabProps) {
  const renderBodyContent = () => {
    if (notesLoading) {
      return <InlineLoading message={MESSAGES.LOADING.NOTES} />;
    }

    if (notesError) {
      return (
        <div className="rounded-xl border border-dashed border-guardian-20 bg-guardian-10 p-6 text-center">
          <p className="text-sm text-rag-red" role="alert">
            {notesError}
          </p>
          <button
            type="button"
            onClick={onRetry}
            className={`mt-4 ${CSS_CLASSES.BUTTONS.CANCEL_MODAL}`}
          >
            Try again
          </button>
        </div>
      );
    }

    if (notes.length === 0) {
      return (
        <div className="rounded-xl border border-dashed border-guardian-20 bg-guardian-10 p-6 text-center">
          <TabNotesIcon className="mx-auto mb-3 h-8 w-8 opacity-60" />
          <p className="text-sm font-medium text-guardian-body">
            {MESSAGES.EMPTY_STATES.NO_NOTES}
          </p>
          <p className={`mt-1 ${CSS_CLASSES.TEXT.MUTED}`}>
            {MESSAGES.EMPTY_STATES.NO_NOTES_HELPER}
          </p>
          <button
            type="button"
            onClick={onAddNote}
            className={`mt-4 ${CSS_CLASSES.BUTTONS.SECONDARY_PATIENT}`}
          >
            Add Note
          </button>
        </div>
      );
    }

    return (
      <div className="min-h-0 flex-1 overflow-y-auto pr-1">
        {notes.map((note) => (
          <div
            key={note.id}
            className="group flex flex-col items-start gap-2.5 border-b border-guardian-10 pb-2.5 pt-2.5 first:pt-0 sm:flex-row sm:flex-wrap sm:items-start"
          >
            <div className="flex min-w-0 w-full flex-1 flex-col gap-0">
              <div className="text-[12px] leading-4 tracking-[0.4px] text-guardian-header">
                {formatNoteDateTime(note.updatedAt ?? note.date)}
              </div>
              <div className="whitespace-pre-line break-words text-[14px] leading-5 tracking-[-0.013em] text-guardian-80">
                {note.text}
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2 self-end opacity-100 focus-within:opacity-100 sm:self-auto sm:opacity-0 sm:transition-opacity sm:group-hover:opacity-100 sm:group-focus-within:opacity-100">
              <button
                type="button"
                onClick={() => note.id && onEditNote(note.id, note.text)}
                className="rounded p-1 transition-colors hover:bg-guardian-10"
                title="Edit note"
              >
                <NoteEditIcon className="h-5 w-5" />
              </button>
              <button
                type="button"
                onClick={() => note.id && onDeleteNote(note.id)}
                className="rounded p-1 transition-colors hover:bg-guardian-10"
                title="Delete note"
              >
                <NoteDeleteIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      {renderBodyContent()}
    </div>
  );
}
